'use client';

import { useQuery } from '@tanstack/react-query';

import { fetchPrV2CubicReviewSummary } from './fetch-pr-v2';
import { PR_V2_STANDARD_STALE_TIME_MS } from './query-config';
import { prV2CubicReviewSummaryQueryKey } from './query-keys';

import { useDeferredQueryEnabled } from '@/hooks/use-deferred-query-enabled';

export function usePrV2CubicReviewSummary(
  owner: string,
  repo: string,
  number: string,
  options?: {
    enabled?: boolean;
    /**
     * Whether to wait out the PR page's startup window before fetching. Defaults to true. A
     * surface that mounts on demand, such as a hover card, shows the counts as soon as it opens.
     */
    deferred?: boolean;
  },
) {
  // Badge/severity data is below the fold; keep it out of the PR page's
  // startup request window (cached data still renders immediately).
  const isPastStartupWindow = useDeferredQueryEnabled();
  const isDeferred = (options?.deferred ?? true) && !isPastStartupWindow;

  return useQuery({
    queryKey: prV2CubicReviewSummaryQueryKey(owner, repo, number),
    queryFn: ({ signal }) => fetchPrV2CubicReviewSummary(owner, repo, number, signal),
    enabled: Boolean(owner && repo && number) && (options?.enabled ?? true) && !isDeferred,
    // Override the global previous-data placeholder so badge counts stay tied
    // to the current PR instead of flashing the previous one during navigation.
    placeholderData: () => undefined,
    staleTime: PR_V2_STANDARD_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}
